<script lang="ts" setup>
  import { useForwardProps } from 'radix-vue'
  import { cva } from 'class-variance-authority'
  import { inject } from 'vue'
  import type { IAccordionItem } from '@/components/ui/accordion/types'
  import type { TIcons } from '@/components/icon/types'

  const MAX_HEADING_LEVEL = 6
  const DEFAULT_HEADING_LEVEL = 2
  const props = withDefaults(defineProps<IAccordionItem>(), { level2n: false })
  const forwardedProps = useForwardProps(props)

  const accordionValue = ref<string | string[] | undefined>(inject('accordionValue'))
  const isMultiple = computed(() => {
    return Array.isArray(accordionValue.value)
  })

  const isOpen = computed(() => {
    if (isMultiple.value) {
      return accordionValue.value?.includes(props.value)
    }

    return accordionValue.value === props.value
  })

  const iconName = computed<TIcons>(() => `common/${isOpen.value ? 'remove' : 'add'}`)

  const accordionItemBase = computed(() =>
    cva(
      'border-l-2 border-t border-solid border-divider border-l-transparent first:border-t-0 focus-visible:outline-none',
      {
        variants: {
          level2n: {
            false: 'data-[state=open]:border-l-bt-green',
            true: 'data-[state=open]:bg-grey data-[state=open]:border-l-transparent'
          }
        }
      }
    )({
      level2n: props.level2n
    })
  )

  const currentLevel = computed(() => {
    const parentLevel = inject('accordionLevel', DEFAULT_HEADING_LEVEL)
    return Math.min(parentLevel + 1, MAX_HEADING_LEVEL)
  })

  provide('accordionLevel', currentLevel.value)
</script>

<template>
  <AccordionItem
    :class="accordionItemBase"
    v-bind="forwardedProps"
  >
    <AccordionHeader
      :id="props.anchor"
      class="mb-0 flex scroll-mt-36 md:scroll-mt-60"
      as="div"
    >
      <AccordionTrigger
        v-if="currentLevel"
        class="group flex flex-1 cursor-pointer items-center justify-between gap-4 p-4 text-left"
      >
        <component
          :is="`h${currentLevel}`"
          class="mb-0"
        >
          <slot name="title" />
        </component>
        <div class="flex size-8 flex-shrink-0 items-center justify-center rounded-full border border-bt-blue-ui">
          <Icon
            class="text-bt-blue-ui transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-180"
            :name="iconName"
          />
        </div>
      </AccordionTrigger>
    </AccordionHeader>
    <AccordionContent
      class="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    >
      <div class="p-4 sm:px-5">
        <slot name="content" />
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
